import PropTypes from "prop-types";
import * as React from "react";
import {
  QUEUE,
  fileProblem,
  initialQueue,
  movedAnnouncement,
  moveImage,
  queueReducer,
  removalNote,
  toFront,
  uploadAnnouncement,
} from "../../domain/shopImageEditor";
import { useInfluencerCharacters, useShopImages } from "../../hooks";
import { AddIcon, Dialog, Typography, WarningIcon } from "../../themeV2";
import shared from "../../themeV2/shared.module.css";
import { generateId } from "../../util/generateId";
import BannerSection from "./BannerSection";
import CharacterGrid from "./CharacterGrid";
import { tileName } from "./CharacterTile";
import CropStep from "./CropStep";
import DetailsDialog from "./DetailsDialog";
import EditorActions from "./EditorActions";
import FilePicker from "./FilePicker";
import HeadshotSection from "./HeadshotSection";
import ImageSection from "./ImageSection";
import readImageFile from "./readImageFile";
import RemoveDialog from "./RemoveDialog";
import css from "./ShopImagesEditor.module.css";

const FALLBACK_ERROR = "Something went wrong. Please try again.";

const SAVE_LABEL = Object.freeze({
  headshot: "Save headshot",
  character: "Upload all",
});

const UPLOAD_REQUEST = Object.freeze({
  headshot: (item) => ["setHeadshot", { image: item.dataUrl, crop: item.crop }],
  character: (item) => [
    "addCharacter",
    {
      image: item.dataUrl,
      crop: item.crop,
      label: item.label ?? undefined,
      influencerCharacterId: item.influencerCharacterId ?? undefined,
    },
  ],
});

const requestFor = (item) =>
  item.recropOf
    ? ["recrop", { imageId: item.recropOf, crop: item.crop }]
    : UPLOAD_REQUEST[item.kind](item);

const messageOf = (error) => error?.message || FALLBACK_ERROR;

const countOf = (outcomes, outcome) =>
  outcomes.filter((each) => each === outcome).length;

const readFiles = async (files) => {
  const refused = files
    .filter(fileProblem)
    .map((file) => `${file.name}: ${fileProblem(file).message}`);
  const settled = await Promise.allSettled(
    files
      .filter((file) => !fileProblem(file))
      .map(async (file) => ({
        id: generateId(),
        fileName: file.name,
        ...(await readImageFile(file)),
      })),
  );
  const unreadable = settled
    .map((result, index) => [result, files[index]])
    .filter(([result]) => result.status === "rejected")
    .map(([result, file]) => `${file.name}: ${messageOf(result.reason)}`);

  return {
    items: settled
      .filter((result) => result.status === "fulfilled")
      .map((result) => result.value),
    problems: [...refused, ...unreadable],
  };
};

export default function ShopImagesEditor({
  open,
  onClose,
  influencerId,
  isCollection = false,
  initialSection = "headshot",
  onHeadshotChange,
}) {
  const { images, perform } = useShopImages(influencerId);
  const { characters: roles = [] } = useInfluencerCharacters(
    isCollection ? null : influencerId,
  );
  const [queue, dispatch] = React.useReducer(queueReducer, initialQueue);
  const [session, setSession] = React.useState(null);
  const [announcement, setAnnouncement] = React.useState("");
  const [problems, setProblems] = React.useState([]);
  const [detailsFor, setDetailsFor] = React.useState(null);
  const [removing, setRemoving] = React.useState(null);
  const headingRefs = {
    headshot: React.useRef(null),
    characters: React.useRef(null),
    banner: React.useRef(null),
  };

  const characters = images.character;
  const imageIds = characters.map((image) => image.imageId);
  const sessionItems = session
    ? session.ids
        .map((id) => queue.items.find((item) => item.id === id))
        .filter(Boolean)
    : [];
  const activeItem = sessionItems[session?.index ?? 0] ?? null;
  const activeId = activeItem?.id;
  const handleCrop = React.useCallback(
    (crop) => dispatch({ type: QUEUE.CROPPED, id: activeId, crop }),
    [activeId],
  );
  const handleDetailsChange = React.useCallback(
    (details) =>
      dispatch({ type: QUEUE.DETAILS_CHANGED, id: activeId, details }),
    [activeId],
  );
  const isOutsideSession = (item) => !session?.ids.includes(item.id);
  const pendingOf = (kind) =>
    queue.items.filter(
      (item) =>
        item.kind === kind && item.status !== "ready" && isOutsideSession(item),
    );

  const runAction = async (action, args, success) => {
    try {
      await perform(action, args);
      setProblems([]);
      setAnnouncement(success);
    } catch (error) {
      setProblems([messageOf(error)]);
    }
  };

  const uploadItems = async (items) => {
    const outcomes = await items.reduce(async (previous, item) => {
      const finished = await previous;
      const [action, args] = requestFor(item);
      dispatch({ type: QUEUE.UPLOAD_STARTED, id: item.id });
      const outcome = await perform(action, args).then(
        (result) => {
          dispatch({
            type: QUEUE.UPLOAD_SUCCEEDED,
            id: item.id,
            warnings: result?.warnings ?? [],
          });
          return "done";
        },
        (error) => {
          dispatch({
            type: QUEUE.UPLOAD_FAILED,
            id: item.id,
            message: messageOf(error),
          });
          return "failed";
        },
      );
      return [...finished, outcome];
    }, Promise.resolve([]));

    setAnnouncement(
      uploadAnnouncement({
        done: countOf(outcomes, "done"),
        failed: countOf(outcomes, "failed"),
      }),
    );
    dispatch({ type: QUEUE.CLEARED_DONE });

    if (
      countOf(outcomes, "done") > 0 &&
      items.some((item) => item.kind === "headshot")
    ) {
      onHeadshotChange?.();
    }
  };

  const startSession = (kind, items) => {
    dispatch({ type: QUEUE.FILES_QUEUED, kind, files: items });
    setSession({ kind, ids: items.map((item) => item.id), index: 0 });
  };

  const queueFiles = (kind) => async (files) => {
    const { items, problems: found } = await readFiles(files);
    setProblems(found);
    if (items.length > 0) startSession(kind, items);
  };

  const replaceBanner = async (files) => {
    const { items, problems: found } = await readFiles(files);
    setProblems(found);
    if (items.length > 0) {
      await runAction(
        "setBanner",
        { image: items[0].dataUrl },
        "Banner updated.",
      );
    }
  };

  const startRecrop = async (image) => {
    try {
      const { dataUrl, imageSize } = await readImageFile(image.sourceUrl);
      startSession(image.kind, [
        {
          id: generateId(),
          fileName: image.kind === "headshot" ? "Headshot" : tileName(image),
          dataUrl,
          imageSize,
          crop: image.crop ?? undefined,
          recropOf: image.imageId,
        },
      ]);
    } catch (error) {
      setProblems([messageOf(error)]);
    }
  };

  const finishSession = () => {
    const items = sessionItems;
    setSession(null);
    uploadItems(items);
  };

  const cancelSession = () => {
    session.ids.forEach((id) => dispatch({ type: QUEUE.DISCARDED, id }));
    setSession(null);
  };

  const step = (delta) =>
    setSession((current) => ({ ...current, index: current.index + delta }));

  const reorder = (nextIds, image) =>
    runAction(
      "reorderCharacters",
      { imageIds: nextIds },
      movedAnnouncement(image, nextIds.indexOf(image.imageId)),
    );

  const handleTileAction = (action, image) =>
    ({
      makePrimary: () => reorder(toFront(imageIds, image.imageId), image),
      moveEarlier: () => reorder(moveImage(imageIds, image.imageId, -1), image),
      moveLater: () => reorder(moveImage(imageIds, image.imageId, 1), image),
      editDetails: () => setDetailsFor(image),
      recrop: () => startRecrop(image),
      remove: () => setRemoving(image),
    }[action]());

  const handleDragReorder = (nextIds, activeId) =>
    reorder(
      nextIds,
      characters.find((image) => image.imageId === activeId),
    );

  const retry = (id) => {
    const item = queue.items.find((each) => each.id === id);
    dispatch({ type: QUEUE.RETRIED, id });
    uploadItems([item]);
  };

  const discard = (id) => dispatch({ type: QUEUE.DISCARDED, id });

  const saveDetails = async (image, details) => {
    setDetailsFor(null);
    await runAction(
      "updateCharacter",
      { imageId: image.imageId, ...details },
      "Details saved.",
    );
  };

  const confirmRemove = async (image) => {
    setRemoving(null);
    await runAction(
      "removeCharacter",
      { imageId: image.imageId },
      `${tileName(image)} removed.`,
    );
  };

  const isCropping = Boolean(session && activeItem);
  const isFirstStep = session?.index === 0;
  const isLastStep = session?.index === sessionItems.length - 1;

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Popup size="full" initialFocus={headingRefs[initialSection]}>
        <Dialog.AppBar
          end={
            isCropping && (
              <Dialog.Description>
                {`Image ${session.index + 1} of ${sessionItems.length}`}
              </Dialog.Description>
            )
          }
        >
          <Dialog.Title>Shop images</Dialog.Title>
        </Dialog.AppBar>

        <Dialog.Body>
          <p className={shared.visuallyHidden} aria-live="polite">
            {announcement}
          </p>

          {problems.length > 0 && (
            <div role="alert" className={css.problems}>
              {problems.map((problem) => (
                <p key={problem} className={css.problem}>
                  <WarningIcon aria-hidden="true" className={css.inlineIcon} />
                  {problem}
                </p>
              ))}
            </div>
          )}

          {isCropping ? (
            <CropStep
              key={activeItem.id}
              item={activeItem}
              roles={roles}
              onCrop={handleCrop}
              onDetailsChange={handleDetailsChange}
            />
          ) : (
            <div className={css.sections}>
              <HeadshotSection
                ref={headingRefs.headshot}
                headshot={images.headshot}
                pending={pendingOf("headshot")}
                onFiles={queueFiles("headshot")}
                onRecrop={startRecrop}
                onRetry={retry}
              />

              {!isCollection && (
                <ImageSection
                  id="characters"
                  ref={headingRefs.characters}
                  title="Character images"
                  description="The first image is shown on cards across the site."
                  actions={
                    <FilePicker
                      inputLabel="Add character images"
                      multiple
                      leading={<AddIcon />}
                      onFiles={queueFiles("character")}
                    >
                      Add images
                    </FilePicker>
                  }
                >
                  {characters.length + pendingOf("character").length > 0 ? (
                    <CharacterGrid
                      characters={characters}
                      pending={pendingOf("character")}
                      onAction={handleTileAction}
                      onReorder={handleDragReorder}
                      onRetry={retry}
                      onDiscard={discard}
                    />
                  ) : (
                    <Typography variant="body-md" color="variant">
                      No character images yet.
                    </Typography>
                  )}
                </ImageSection>
              )}

              <BannerSection
                ref={headingRefs.banner}
                banner={images.banner}
                onFiles={replaceBanner}
                onRemove={() =>
                  runAction("removeBanner", {}, "Banner removed.")
                }
              />
            </div>
          )}
        </Dialog.Body>

        <EditorActions
          step={
            isCropping
              ? {
                  isFirst: isFirstStep,
                  isLast: isLastStep,
                  saveLabel: activeItem.recropOf
                    ? "Save crop"
                    : SAVE_LABEL[session.kind],
                }
              : null
          }
          onBack={isFirstStep ? cancelSession : () => step(-1)}
          onNext={() => step(1)}
          onSave={finishSession}
          onDone={onClose}
        />

        <DetailsDialog
          image={detailsFor}
          roles={roles}
          onSave={saveDetails}
          onClose={() => setDetailsFor(null)}
        />
        <RemoveDialog
          image={removing}
          note={removing ? removalNote(characters, removing.imageId) : null}
          onConfirm={confirmRemove}
          onClose={() => setRemoving(null)}
        />
      </Dialog.Popup>
    </Dialog.Root>
  );
}

ShopImagesEditor.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  influencerId: PropTypes.number,
  isCollection: PropTypes.bool,
  initialSection: PropTypes.oneOf(["headshot", "characters", "banner"]),
  onHeadshotChange: PropTypes.func,
};
